/* eslint-disable no-nested-ternary */
import React from "react";
import dynamic from "next/dynamic";

import Image from "components/Image/CustomImage";

import { makeStyles } from "tss-react/mui";
import ReviewDetails from "components/Reviews/ReviewDetails";
import TimerIcon from "@mui/icons-material/Timer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LinearProgress from "@mui/material/LinearProgress";

import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faYoutube } from "@fortawesome/free-brands-svg-icons/faYoutube";

/* const Modal = dynamic(() => import('@mui/material/Modal'));
const Backdrop = dynamic(() => import('@mui/material/Backdrop'));
const Fade = dynamic(() => import('@mui/material/Fade')); */

import { PRODUCT_PAGE_CONTENT_RESPONSIVE_SETTINGS } from "settings/global-website-config.js";

import {
  reviewInfoIcon,
  titleFont,
  textMutedColor,
  cardShadow,
  buttonHoverShadow,
  noWrap,
  grayColor,
} from "../../assets/jss/nextjs-material-kit-pro";

const ReactPlayer = dynamic(() => import("react-player/lazy"));
const CustomCarousel = dynamic(() =>
  import("components/Feedblocks/CustomCarousel.js")
);
const useStyles = makeStyles()((theme) => ({
  videoModal: {
    display: "inline-table",
    margin: "auto",

    width: "70vw",
    [theme.breakpoints.down("lg")]: {
      width: "80vw",
    },
    [theme.breakpoints.down("md")]: {
      width: "90vw",
    },
  },
  videoPlayerContainer: {
    boxShadow: theme.shadows[8],
    backgroundColor: theme.palette.common.black,
    paddingTop: "56.25%",
    position: "relative",
  },
  reactPlayer: { position: "absolute", top: 0, left: 0 },

  reviewInfoIcon,
  infoBadgesContainer: {
    display: "flex",
    width: "100%",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    padding: "0px 4px",
  },
  infoBadges: {
    color: textMutedColor,
    display: "flex",
    alignItems: "flex-start",
    maxWidth: "fit-content",
    "& p": {
      fontSize: "0.75rem",
      margin: theme.spacing(0.25, 0),
      whiteSpace: "noWrap",
    },
  },
  reactPlayerPreview: {
    filter: "brightness(50%)",
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",
  },
  playButton: {
    color: theme.palette.secondary.main,
    background: "radial-gradient(white 50%, transparent 50%)", // Fills the Play part white
    position: "absolute",
    top: "50%",
    left: "50%",
    zIndex: 100,
    transform: "translate(-50%,-50%)", // Positions in the centre
    cursor: "pointer",
    // pointerEvents: 'none',
    transition: "all 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms !important",
  },
  bigExpand: { "&:hover": { fontSize: "3.5em" } },
  smallExpand: { "&:hover": { fontSize: "2.5em" } },
  playerWrapper: {
    position: "relative",
    paddingTop: "50%", //'18.5%' /* Player ratio: 33 / (1280 / 720) */,
    borderRadius: theme.spacing(0.75),
    boxShadow: cardShadow,
    ...buttonHoverShadow,
  },
  scrollHorizontalContainer: {
    display: "flex",
    alignItems: "flexStart",
  },
  progressBar: {
    position: "absolute",
    bottom: 0,
    zIndex: 15,
    width: "100%",
    height: "6px",
    borderRadius: "0px 0px 5px 5px",
  },
  noWrap,
  playersWrapper: {
    // justifyContent: 'space-around',
    paddingTop: "15px",
    paddingBottom: "15px",
    //   marginLeft: '-10px', // offset margin between vids
    //   marginRight: '-10px', // offset margin between vids
  },
  reviewerDetails: {
    color: grayColor[1],
    textDecoration: "none",
    ...titleFont,
    //`"Roboto Slab", "Times New Roman", serif`,
  },
}));

// *** Displays up to 3 videos side by side on fullscreen width ***

// NB currently only supports YouTube but can be changed by altering the import at the top of this file
const ViewsCount = (video) => (
  <p>
    <VisibilityIcon />
    {video.reviewDetails.videoReview.viewCount.toLocaleString()}
  </p>
);

const Duration = (video) => (
  <p>
    <TimerIcon />
    {video.reviewDetails.videoReview.duration}
  </p>
);
export default function VideoPlayer(props) {
  const {
    videos,
    productId,
    basic,
    noCarousel,
    //showTitle,
    hideChannel,
    scrollHorizontal,
  } = props;

  const { classes, cx } = useStyles();

  const [progress, setProgress] = React.useState({});

  const [modalUrl, setModalUrl] = React.useState(null);

  const storedProgressKey = /*  productId + */ "geerly-progress";

  React.useEffect(() => {
    if (localStorage) {
      const storedProgress = localStorage.getItem(storedProgressKey);

      if (storedProgress) {
        setProgress(JSON.parse(storedProgress));
      }
    }
  }, [storedProgressKey]);

  const videosArray = videos.map((video) => {
    const url = video.reviewDetails?.videoReview.url || video.url;
    return (
      <Tooltip
        key={url}
        title={video.reviewDetails.reviewHeadline}
        enterDelay={1000}
      >
        <Box
          sx={{
            px: noCarousel ? 0 : 2,
            py: 0.5,
            display: "inline-block",
            width: "100%" /*'160px' */,
          }}
        >
          <div
            className={cx({
              [classes.playerWrapper]: true,
            })}
          >
            {!modalUrl && (
              <Fade in={!modalUrl}>
                <FontAwesomeIcon
                  icon={faYoutube}
                  size={basic ? "2x" : "3x"}
                  className={cx({
                    [classes.playButton]: true,
                    [classes.bigExpand]: !basic,
                    [classes.smallExpand]: basic,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalUrl(url);
                  }}
                />
              </Fade>
            )}
            {progress[url]?.played ? (
              <LinearProgress
                variant="determinate"
                value={progress[url].played * 100}
                className={classes.progressBar}
                color="secondary"
              />
            ) : (
              ""
            )}
            <Image
              src={video.reviewDetails?.videoReview.thumbnails.high.url}
              fill
              style={{ objectFit: "cover" }}
              alt={video.reviewDetails?.reviewHeadline}
              onClick={(e) => {
                e.stopPropagation();
                setModalUrl(url);
              }}
              className={classes.reactPlayerPreview}
            />
          </div>
          {video.reviewDetails && (
            <>
              <div className={classes.infoBadgesContainer}>
                <span
                  className={`${classes.infoBadges} ${classes.reviewInfoIcon}`}
                >
                  {ViewsCount(video)}
                </span>
                <span
                  className={`${classes.infoBadges} ${classes.reviewInfoIcon}`}
                >
                  {Duration(video)}
                </span>
              </div>
              <div className={classes.reviewerDetails}>
                {/*  {showTitle && video.reviewDetails.reviewHeadline} */}
                <ReviewDetails
                  review={video}
                  url={url}
                  productId={productId}
                  linkToAthlete={basic}
                  basic={basic}
                  hideChannel={hideChannel}
                />
              </div>
            </>
          )}
        </Box>
      </Tooltip>
    );
  });

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.videoModal}
        open={modalUrl || false}
        onClose={() => setModalUrl(null)}
        closeAfterTransition
        BackdropComponent={Backdrop}
      >
        <Fade in={Boolean(modalUrl)}>
          <div className={classes.videoPlayerContainer}>
            <ReactPlayer
              className={classes.reactPlayer}
              playing={true}
              url={modalUrl}
              light={false}
              config={{
                youtube: {
                  playerVars: {
                    start: Math.round(progress[modalUrl]?.playedSeconds || 0),
                  },
                },
              }}
              controls
              width="100%"
              height="100%"
              onClick={() => {
                setModalUrl(null);
              }}
              onProgress={(p) => {
                setProgress({
                  ...progress,
                  [modalUrl]: p,
                });

                localStorage.setItem(
                  storedProgressKey,
                  JSON.stringify(progress)
                );
              }}
            />
          </div>
        </Fade>
      </Modal>
      <div
        className={cx({
          [classes.playersWrapper]: !basic && !noCarousel,
          [classes.noWrap]: false,
          [classes.scrollHorizontalContainer]: scrollHorizontal,
        })}
      >
        {!basic && !noCarousel ? (
          <CustomCarousel
            responsiveSettings={PRODUCT_PAGE_CONTENT_RESPONSIVE_SETTINGS}
          >
            {videosArray}
          </CustomCarousel>
        ) : (
          videosArray
        )}
      </div>
    </>
  );
}
